import React, { useState, useEffect } from 'react';
import { 
  Edit, Trash2, Image as ImageIcon, Search, 
  ChevronRight, ChevronDown, ArrowLeft, Upload, X, Save, FolderTree, AlertCircle, LayoutList 
} from 'lucide-react';

// --- Types ---
interface Category {
  id: number;
  name: string;
  image_url?: string | null;
  subCategories?: Category[];
}

const CategoryListPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selection & Expansion
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<number[]>([]);

  // Edit Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null);
  
  // Form State
  const [formData, setFormData] = useState({ name: '', image: null as File | null, preview: null as string | null });

  // --- Fetch Data ---
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch('/nodejs/api/categories');
      const data = await response.json();
      setCategories(data);
      
      if (selectedCategory) {
        const updated = data.find((c: Category) => c.id === selectedCategory.id);
        if (updated) setSelectedCategory(updated);
        else setSelectedCategory(null);
      }
    } catch (err) { console.error(err); } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCategories(); }, []);

  // --- Helpers ---
  const toggleNode = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedNodes(prev => prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id]);
  };

  // --- DELETE Handler ---
  const handleDelete = async (id: number, name: string, e?: React.MouseEvent) => {
    if(e) e.stopPropagation();
    
    if (!window.confirm(`⚠️ Are you sure you want to delete "${name}"?\n\nThis will remove the category and all sub-categories.`)) return;

    try {
      const res = await fetch(`/nodejs/api/categories/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchCategories();
        if (selectedCategory?.id === id) setSelectedCategory(null);
      } else {
        alert("Failed to delete.");
      }
    } catch (e) { console.error(e); alert("Server error"); }
  };

  // --- EDIT Handlers ---
  const openEditModal = (category: Category, e?: React.MouseEvent) => {
    if(e) e.stopPropagation();
    setCategoryToEdit(category);
    setFormData({ 
      name: category.name, 
      image: null, 
      preview: category.image_url || null 
    });
    setShowEditModal(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({ ...prev, image: file, preview: URL.createObjectURL(file) }));
    }
  };

  const handleUpdateSubmit = async () => {
    if (!categoryToEdit || !formData.name) return alert("Name is required");

    const payload = new FormData();
    payload.append('name', formData.name);
    if (formData.image) payload.append('categoryImage', formData.image);
    
    try {
      const res = await fetch(`/nodejs/api/categories/${categoryToEdit.id}`, { 
        method: 'PUT', 
        body: payload 
      });

      if (res.ok) {
        setShowEditModal(false);
        fetchCategories(); 
      } else {
        alert("Update failed");
      }
    } catch (e) { console.error(e); alert("Error occurred"); }
  };

  // --- Recursive Tree Component ---
  const RenderTreeItem = ({ node, level }: { node: Category, level: number }) => {
    const hasChildren = node.subCategories && node.subCategories.length > 0;
    const isExpanded = expandedNodes.includes(node.id);

    return (
      <div className="select-none animate-in fade-in slide-in-from-top-1 duration-200">
        <div 
          className={`
            group flex items-center justify-between p-2.5 rounded-lg transition-all mb-1 cursor-default border border-transparent
            ${level === 0 ? 'bg-white border-gray-100 hover:border-[#006d5b]/30' : 'hover:bg-gray-50'}
          `}
          style={{ marginLeft: `${level * 20}px` }}
        >
          <div className="flex items-center gap-3 flex-1 overflow-hidden">
            {/* Expand Icon */}
            <div 
              onClick={(e) => hasChildren && toggleNode(node.id, e)}
              className={`p-1 rounded-md transition-colors ${hasChildren ? 'cursor-pointer hover:bg-gray-100 text-gray-500' : 'opacity-0'}`}
            >
               {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>

            {/* Icon */}
            <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
               {node.image_url ? <img src={node.image_url} className="w-full h-full object-cover" alt="icon" /> : <ImageIcon size={14} className="text-gray-300"/>}
            </div>

            {/* Name */}
            <div className="flex flex-col">
                <span className="text-sm font-bold text-gray-800 truncate">{node.name}</span>
                {hasChildren && <span className="text-[9px] text-gray-400 font-bold uppercase">{node.subCategories?.length} Sub-items</span>}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
             <button onClick={(e) => openEditModal(node, e)} className="p-1.5 text-gray-500 hover:text-[#006d5b] hover:bg-[#006d5b]/5 rounded-md"><Edit size={14} /></button>
             <button onClick={(e) => handleDelete(node.id, node.name, e)} className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-md"><Trash2 size={14} /></button>
          </div>
        </div>

        {/* Recursive Children with Line */}
        {hasChildren && isExpanded && (
          <div className="border-l-2 border-gray-100 ml-[15px] pl-1">
             {node.subCategories!.map(child => (
               <RenderTreeItem key={child.id} node={child} level={level + 1} />
             ))}
          </div>
        )}
      </div>
    );
  };

  const filteredCategories = categories.filter(cat => 
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white font-sans animate-fade-in-up flex flex-col h-screen overflow-hidden">
      <style>{` .no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; } `}</style>

      {/* 1. HEADER (Fixed) */}
      <div className="px-6 py-6 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-4">
           <div className="w-12 h-12 bg-[#006d5b]/10 rounded-xl flex items-center justify-center text-[#006d5b] shadow-sm">
              <LayoutList size={24} />
           </div>
           <div>
             <h2 className="text-2xl font-black text-gray-900 tracking-tight">Category Manager</h2>
             <p className="text-xs text-gray-500 font-bold mt-0.5">Organize and edit your catalog structure.</p>
           </div>
        </div>
      </div>

      {/* 2. SPLIT VIEW CONTENT (Scrollable) */}
      <div className="flex flex-1 overflow-hidden">
        
        {/* LEFT PANEL: Root List */}
        <div className={`
            flex-col w-full md:w-80 lg:w-96 border-r border-gray-100 bg-white h-full flex-shrink-0 transition-all duration-300
            ${selectedCategory ? 'hidden md:flex' : 'flex'} 
        `}>
           {/* Search */}
           <div className="p-4 border-b border-gray-50">
              <div className="relative">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#006d5b]" size={16} />
                 <input 
                   type="text" 
                   placeholder="Search categories..." 
                   className="w-full bg-gray-50 border-none rounded-xl py-3 pl-10 pr-4 text-sm font-bold text-gray-700 focus:ring-2 focus:ring-[#006d5b]/10 outline-none transition-all placeholder:text-gray-400"
                   onChange={(e) => setSearchTerm(e.target.value)}
                 />
              </div>
           </div>

           {/* List */}
           <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
              {loading ? (
                 <div className="flex flex-col items-center justify-center h-40 text-gray-400 text-xs">Loading...</div>
              ) : (
                 filteredCategories.map(cat => (
                    <div 
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat)}
                      className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all border ${
                        selectedCategory?.id === cat.id 
                          ? 'bg-[#006d5b] text-white border-[#006d5b] shadow-md shadow-[#006d5b]/20' 
                          : 'bg-white border-gray-100 hover:border-[#006d5b]/30 hover:bg-gray-50'
                      }`}
                    >
                       <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden bg-white/10`}>
                             {cat.image_url ? (
                                <img src={cat.image_url} className="w-full h-full object-cover" alt={cat.name} />
                             ) : (
                                <ImageIcon size={18} className={selectedCategory?.id === cat.id ? 'text-white' : 'text-gray-300'} />
                             )}
                          </div>
                          <div>
                             <p className="text-sm font-black line-clamp-1">{cat.name}</p>
                             <p className={`text-[10px] font-bold uppercase ${selectedCategory?.id === cat.id ? 'text-white/80' : 'text-gray-400'}`}>
                                {cat.subCategories?.length || 0} Sub-Items
                             </p>
                          </div>
                       </div>
                       <ChevronRight size={16} className={`transition-transform ${selectedCategory?.id === cat.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`} />
                    </div>
                 ))
              )}
           </div>
        </div>

        {/* RIGHT PANEL: Detailed View */}
        <div className={`
            flex-1 bg-gray-50/50 flex-col h-full overflow-hidden relative
            ${selectedCategory ? 'flex' : 'hidden md:flex items-center justify-center'}
        `}>
           
           {!selectedCategory ? (
              <div className="text-center p-10 opacity-40">
                 <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
                    <FolderTree size={32} />
                 </div>
                 <h3 className="text-lg font-black text-gray-800">No Selection</h3>
                 <p className="text-xs text-gray-500 font-bold mt-1">Select a category from the list to manage.</p>
              </div>
           ) : (
              <div className="flex flex-col h-full">
                 {/* Top Navigation Bar (Mobile only) */}
                 <div className="md:hidden p-4 bg-white border-b border-gray-100 flex items-center gap-2">
                    <button onClick={() => setSelectedCategory(null)} className="p-2 -ml-2 text-gray-500 hover:text-[#006d5b]">
                       <ArrowLeft size={20} />
                    </button>
                    <span className="text-sm font-bold text-gray-900">Back to List</span>
                 </div>

                 {/* Hero Header */}
                 <div className="p-8 bg-white border-b border-gray-100 flex flex-col md:flex-row items-center gap-6 shadow-sm z-10">
                    <div className="w-20 h-20 bg-gray-50 rounded-2xl border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
                       {selectedCategory.image_url ? (
                          <img src={selectedCategory.image_url} className="w-full h-full object-cover" alt="icon" />
                       ) : (
                          <ImageIcon size={32} className="text-gray-300" />
                       )}
                    </div>
                    <div className="text-center md:text-left flex-1">
                       <h2 className="text-2xl font-black text-gray-900">{selectedCategory.name}</h2>
                       <div className="flex items-center justify-center md:justify-start gap-3 mt-2">
                          <span className="bg-[#006d5b]/10 text-[#006d5b] px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider">
                             ID: #{selectedCategory.id}
                          </span>
                       </div>
                    </div>
                    <div className="flex gap-3">
                       <button onClick={(e) => openEditModal(selectedCategory, e)} className="px-4 py-2 border border-gray-200 text-gray-600 hover:border-[#006d5b] hover:text-[#006d5b] rounded-xl text-xs font-bold transition-all flex items-center gap-2">
                          <Edit size={14} /> Edit
                       </button>
                       <button onClick={(e) => handleDelete(selectedCategory.id, selectedCategory.name, e)} className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl text-xs font-bold transition-all flex items-center gap-2">
                          <Trash2 size={14} /> Delete
                       </button>
                    </div>
                 </div>

                 {/* Tree Content Area */}
                 <div className="flex-1 overflow-y-auto p-6 md:p-10 no-scrollbar">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 ml-1">Hierarchy Structure</h3>
                    
                    {selectedCategory.subCategories && selectedCategory.subCategories.length > 0 ? (
                       <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                          {selectedCategory.subCategories.map(sub => (
                             <RenderTreeItem key={sub.id} node={sub} level={0} />
                          ))}
                       </div>
                    ) : (
                       <div className="py-16 text-center border-2 border-dashed border-gray-200 rounded-2xl bg-white">
                          <AlertCircle className="mx-auto text-gray-300 mb-3" size={32} />
                          <p className="text-gray-400 font-bold text-sm">Empty Category</p>
                          <p className="text-[10px] text-gray-400 mt-1">No sub-categories found inside.</p>
                       </div>
                    )}
                 </div>
              </div>
           )}
        </div>
      </div>

      {/* --- EDIT MODAL --- */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#001e1d]/60 backdrop-blur-sm p-4 animate-fade-in">
           <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-zoom-in">
              <div className="p-5 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-sm font-black text-gray-900 uppercase tracking-wide">Edit Category</h3>
                 <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-red-500 transition-colors">
                    <X size={18} />
                 </button>
              </div>
              
              <div className="p-6 space-y-6">
                 {/* Image */}
                 <div className="flex justify-center">
                    <div className="relative w-24 h-24 group cursor-pointer">
                       <img 
                         src={formData.preview || 'https://via.placeholder.com/150'} 
                         className="w-full h-full object-cover rounded-xl border border-gray-200 shadow-sm" 
                         alt="Preview"
                       />
                       <label className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                          <Upload size={20} />
                          <input type="file" className="hidden" onChange={handleFileChange} accept="image/*" />
                       </label>
                    </div>
                 </div>

                 {/* Name */}
                 <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Name</label>
                    <input 
                      value={formData.name}
                      onChange={e => setFormData(prev => ({...prev, name: e.target.value}))}
                      className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-bold text-gray-900 focus:bg-white focus:border-[#006d5b] outline-none transition-all"
                      autoFocus
                    />
                 </div>

                 <button 
                   onClick={handleUpdateSubmit} 
                   className="w-full bg-[#006d5b] hover:bg-[#005a4b] text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg shadow-[#006d5b]/20 flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                 >
                    <Save size={14} /> Update Changes
                 </button>
              </div>
           </div>
        </div>
      )}

    </div>
  );
};

export default CategoryListPage;